import { Dialog, FormDatePicker, FormInput, FormPhoneInput } from "@/components";
import { Badge, Button, Input, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui";
import { cn } from "@/lib/utils";
import { useAddTenantMutation, useSearchTenantCandidates } from "@/services";
import { zodResolver } from "@hookform/resolvers/zod";
import { Search, UserPlus } from "lucide-react";
import moment from "moment";
import { Fragment, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "@/components/Toast";
import { z } from "zod";

const startOfDay = (v?: string) => (v ? moment(v).format("YYYY-MM-DDT00:00:00") : null);
const endOfDay = (v?: string) => (v ? moment(v).format("YYYY-MM-DDT23:59:59") : null);
const humanize = (v?: string) => (v ? v.replace(/_/g, " ") : "—");
const errorMessage = (e?: any) =>
  typeof e?.response?.data?.message === "string" ? e.response.data.message : "Something went wrong";

const schema = z
  .object({
    startDate: z.string().min(1, "Please select Start Date."),
    endDate: z.string().optional().nullable(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    phoneNumber: z.string().optional(),
    email: z.string().optional(),
  })
  .refine((d) => !d.endDate || !moment(d.endDate).isBefore(moment(d.startDate), "day"),
    { message: "End Date cannot be before Start Date.", path: ["endDate"] });

interface AddTenantDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  account: any;           // current title (ownershipId, propertyName, ownershipCode, people)
  communityId: string;
  onAdded: () => void;
}

/**
 * Add Tenant Manually: search existing people by name / phone / email and attach one, or create a new
 * tenant (name, phone, email). Tenants have no portal login in this phase.
 */
const AddTenantDialog = ({ open, setOpen, account, communityId, onAdded }: AddTenantDialogProps) => {
  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState("");
  const [showNewForm, setShowNewForm] = useState(false);
  const form = useForm<any>({
    resolver: zodResolver(schema),
    defaultValues: { startDate: moment().startOf("day").format("YYYY-MM-DDTHH:mm:ss"), endDate: "", firstName: "", lastName: "", phoneNumber: "", email: "" },
  });
  const errors: any = form.formState.errors;

  const { data, isFetching } = useSearchTenantCandidates({ communityId, query });
  const rows: any[] = data?.data || [];
  const searched = query.trim().length >= 2 && !isFetching;

  // Group association rows by user (users without associations come back as a single row)
  const users = useMemo(() => {
    const byUser = new Map<number, any>();
    rows.forEach((r) => {
      if (!byUser.has(r.userId)) byUser.set(r.userId, { ...r, relationships: [] });
      if (r.propertyUserAssociationId) byUser.get(r.userId).relationships.push(r);
    });
    return Array.from(byUser.values());
  }, [rows]);
  const activeOnTitle = useMemo(
    () => new Set((account?.people || []).filter((p: any) => p.active).map((p: any) => p.userId)),
    [account]
  );

  const { mutate: addTenant, isPending } = useAddTenantMutation({
    onError: (e) => toast.error(errorMessage(e)),
    onSuccess: () => {
      toast.success("Tenant attached Successfully");
      setOpen(false);
      onAdded();
    },
  });

  const dates = (v: any) => ({ startDate: startOfDay(v.startDate), endDate: endOfDay(v.endDate) });
  const submit = (body: any) => addTenant({ communityId, ownershipId: String(account.ownershipId), data: body });

  const attachExisting = async (userId: number) => {
    if (!(await form.trigger(["startDate", "endDate"]))) return;
    submit({ userId, ...dates(form.getValues()) });
  };

  const createAndAttach = async () => {
    const v = form.getValues();
    let ok = await form.trigger(["startDate", "endDate"]);
    const required: [string, string][] = [["firstName", "Please enter First Name."], ["phoneNumber", "Please enter Phone Number."], ["email", "Please enter Email."]];
    required.forEach(([f, m]) => { if (!v[f]?.trim()) { form.setError(f, { message: m }); ok = false; } });
    if (v.email?.trim() && !z.string().email().safeParse(v.email.trim()).success) {
      form.setError("email", { message: "This is not a valid email." });
      ok = false;
    }
    if (!ok) return;
    submit({ firstName: v.firstName, lastName: v.lastName || null, phoneNumber: v.phoneNumber, email: v.email, ...dates(v) });
  };

  return (
    <Dialog open={open} setOpen={setOpen} hideFooter
      title={`Add Tenant Manually — ${account?.propertyName || ""} (${account?.ownershipCode || ""})`}
      contentClassName="lg:!max-w-[1100px] lg:!w-[1100px]">
      <FormProvider {...form}>
        <div className="flex flex-col gap-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormDatePicker name="startDate" control={form.control} label="Tenancy Start Date" isRequired error={errors.startDate?.message} />
            <FormDatePicker name="endDate" control={form.control} label="Tenancy End Date" error={errors.endDate?.message} />
          </div>

          <div className="flex flex-col gap-2">
            <p className="text-sm font-medium">Find the tenant</p>
            <div className="flex gap-2">
              <Input
                className="flex-1 bg-white h-[40px]"
                placeholder="Search by name, phone number or email — press Enter"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); setQuery(searchInput.trim()); setShowNewForm(false); } }}
                startIcon={<Search className="w-4 h-4" />}
              />
              <Button type="button" variant="outline" onClick={() => { setQuery(searchInput.trim()); setShowNewForm(false); }}
                disabled={searchInput.trim().length < 2}>Search</Button>
            </div>
            <p className="text-xs text-gray-500">The tenant is attached to this property under the owner's title {account?.ownershipCode}.</p>
          </div>

          {isFetching ? <p className="text-sm text-gray-500">Searching…</p> : <></>}

          {searched && users.length > 0 ? (
            <div className="border rounded-lg overflow-x-auto max-h-[320px] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {["Name", "Phone Number", "Email", "Community", "Property", "Relationship", ""].map((h) => (
                      <TableHead key={h} className="font-semibold text-primary whitespace-nowrap">{h}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => {
                    const rels = u.relationships.length ? u.relationships : [null];
                    const onTitle = activeOnTitle.has(u.userId);
                    return (
                      <Fragment key={u.userId}>
                        {rels.map((r: any, i: number) => (
                          <TableRow key={`${u.userId}-${r?.propertyUserAssociationId || "none"}`} className={i > 0 ? "border-t-0" : ""}>
                            <TableCell className="font-medium">{i === 0 ? [u.firstName, u.lastName].filter(Boolean).join(" ") || "—" : ""}</TableCell>
                            <TableCell>{i === 0 ? u.phoneNumber || "—" : ""}</TableCell>
                            <TableCell>{i === 0 ? u.email || "—" : ""}</TableCell>
                            <TableCell>{r?.communityName || "—"}</TableCell>
                            <TableCell>{r?.propertyName || "—"}</TableCell>
                            <TableCell>
                              {r ? (
                                <Badge variant="outline" className={cn("rounded-md px-2 py-0.5 text-xs", r.active ? "" : "text-gray-500")}>
                                  {humanize(r.relationship)}{r.active ? "" : " (ended)"}
                                </Badge>
                              ) : <span className="text-xs text-gray-500">No relationships</span>}
                            </TableCell>
                            <TableCell className="text-right">
                              {i === 0 ? (onTitle
                                ? <span className="text-xs text-gray-500 whitespace-nowrap">Already on this title</span>
                                : <Button type="button" size="sm" disabled={isPending} onClick={() => attachExisting(u.userId)}>Attach</Button>
                              ) : <></>}
                            </TableCell>
                          </TableRow>
                        ))}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : <></>}

          {searched && users.length === 0 ? (
            <p className="text-sm text-gray-600">No match found for "{query}". Add the tenant as a new person below.</p>
          ) : <></>}

          {searched && users.length > 0 && !showNewForm ? (
            <Button type="button" variant="link" className="self-start p-0" onClick={() => setShowNewForm(true)}>
              <UserPlus className="w-4 h-4" /> None of these — add a new tenant
            </Button>
          ) : <></>}

          {searched && (users.length === 0 || showNewForm) ? (
            <div className="border rounded-lg p-4 flex flex-col gap-4 bg-gray-50">
              <p className="text-sm font-medium">New tenant</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <FormInput name="firstName" control={form.control} label="First Name" isRequired placeholder="Please enter" error={errors.firstName?.message} />
                <FormInput name="lastName" control={form.control} label="Last Name" placeholder="Please enter" />
                <FormPhoneInput name="phoneNumber" control={form.control} label="Phone Number" isRequired placeholder="Please enter" error={errors.phoneNumber?.message} />
                <FormInput name="email" control={form.control} label="Email" isRequired placeholder="Please enter" error={errors.email?.message} />
              </div>
              <p className="text-xs text-gray-500">Tenants don't get a portal login in this phase — no user name or password is needed.</p>
              <Button type="button" className="self-end" disabled={isPending} onClick={createAndAttach}>
                <UserPlus className="w-4 h-4" /> Create &amp; Attach
              </Button>
            </div>
          ) : <></>}
        </div>
      </FormProvider>
    </Dialog>
  );
};

export default AddTenantDialog;
